import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { Identity, MediaRef, Message, Subject } from '../types';
import { errorMessage } from '../lib/db';
import { encodeGroupShareUrl } from '../lib/groupShare';
import { fetchUserDevices } from '../lib/relay';
import {
  getSignalStore,
  sendToDevices,
  signalSafetyNumber,
} from '../lib/signal';
import { safetyEmojis } from '../lib/safetyEmoji';
import { ScheduleControl } from '../components/ScheduleControl';
import { formatDelivery } from '../lib/schedule';
import { encryptAndUpload } from '../lib/media';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { LetterCompose } from './LetterCompose';
import { LetterReader } from './LetterReader';

type Props = {
  me: Identity;
  subject: Subject;
  messages: Message[];
  onBack: () => void;
  onSent: (m: Message) => void;
  onReact: (m: Message, emoji: string) => void;
};

const REACTION_PALETTE = ['❤️', '👍', '😂', '😮', '🙏', '🪶'];

// Telegram-style quick emoji panel for the composer. Curated, not
// exhaustive — common faces, hearts, hands, and a few that fit the
// app's calm register. Tap one, it inserts at the cursor and the
// panel stays open so you can stack a few.
const COMPOSER_EMOJI = [
  '😀','😄','😅','🤣','🙂','🙃','😉','😍','🥰','😘',
  '🤔','😎','🥲','🥹','😴','😮','😭','😡','🤯','🤗',
  '❤️','🧡','💛','💚','💙','💜','🤍','🖤','💔','✨',
  '👍','👎','👏','🙏','🙌','👋','🤝','🤞','✌️','🤘',
  '🌿','🌱','🌸','🌼','🌞','🌙','🔥','🎉','🥳','☕',
];

/** Decode a base64 string into an ArrayBuffer — needed by signalSafetyNumber. */
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/** Format the libsignal 60-digit decimal fingerprint into 12 groups of 5. */
function formatSafetyDigits(s: string): string {
  return s.match(/.{1,5}/g)?.join(' ') ?? s;
}

export function Conversation({ me, subject, messages, onBack, onSent, onReact }: Props) {
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [letterMode, setLetterMode] = useState(false);
  const [openLetter, setOpenLetter] = useState<Message | null>(null);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [deliverAt, setDeliverAt] = useState<Date | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize composer textarea as the user types. Grows from one line
  // up to ~6 lines; longer messages scroll inside the textarea.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 180);
    el.style.height = `${next}px`;
  }, [draft]);

  function insertEmoji(emoji: string) {
    const el = composerRef.current;
    if (!el) {
      setDraft((d) => d + emoji);
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(next);
    // Restore cursor position after the inserted glyph.
    window.requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + emoji.length;
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        // some mobile browsers throw on setSelectionRange — ignore
      }
    });
  }

  const title =
    subject.kind === 'friend' ? `@${subject.friend.username}` : subject.group.name;

  const subtitle =
    subject.kind === 'group'
      ? `${subject.group.members.length + 1} people · group`
      : null;

  // Safety number is derived from each side's libsignal identity key —
  // the *real* authentication key. The async useEffect computes it on
  // mount; until then we render a "computing…" placeholder.
  //
  // Three terminal states:
  //   - 'ok'           — both sides on Signal; show the 60-digit fingerprint
  //   - 'legacy_only'  — friend hasn't published a Signal bundle yet, no
  //                      cryptographically meaningful safety number exists
  //   - 'error'        — something went wrong loading our own identity key
  // Safety numbers are per-device-pair: one fingerprint for each of the
  // friend's devices ↔ this device. When the friend has only one device
  // (the common case), the card shows a single number. When they have
  // multiple, you can scroll through and verify each one.
  type SafetyEntry = { device_id: string; label: string; digits: string; emojis: string[] };
  const [safety, setSafety] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; entries: SafetyEntry[] }
    | { kind: 'no_devices' }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const [showDigits, setShowDigits] = useState(false);

  useEffect(() => {
    if (subject.kind !== 'friend') return;
    const friend = subject.friend;
    if (!friend.devices || friend.devices.length === 0) {
      setSafety({ kind: 'no_devices' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const store = getSignalStore(me.id);
        const kp = await store.getIdentityKeyPair();
        if (!kp) {
          if (!cancelled) {
            setSafety({
              kind: 'error',
              message: 'Local Signal identity not ready yet. Reopen the app.',
            });
          }
          return;
        }
        const myKeyB64 = arrayBufferToBase64(kp.pubKey);
        const entries: SafetyEntry[] = [];
        for (const device of friend.devices) {
          const remoteKey = base64ToArrayBuffer(device.signal_identity_pubkey_b64);
          const digits = await signalSafetyNumber({
            localDeviceId: me.device_id,
            localIdentityKey: kp.pubKey,
            remoteDeviceId: device.id,
            remoteIdentityKey: remoteKey,
          });
          const emojis = safetyEmojis(myKeyB64, device.signal_identity_pubkey_b64);
          entries.push({ device_id: device.id, label: device.label, digits, emojis });
        }
        if (!cancelled) setSafety({ kind: 'ok', entries });
      } catch (err) {
        if (!cancelled) {
          setSafety({ kind: 'error', message: errorMessage(err) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me.id, me.device_id, subject]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  useEffect(() => {
    if (!attachment) {
      setAttachmentPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachment);
    setAttachmentPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return; // user dismissed the picker — no error
    setError(null);
    // Read the bytes NOW, while the picker's file reference is still
    // valid. On Android (especially photos from Google Photos / cloud
    // providers), the reference goes stale by the time we'd read it at
    // send, throwing "the requested file could not be read." Copying into
    // an in-memory File here sidesteps that entirely.
    try {
      const buf = await file.arrayBuffer();
      const inMemory = new File([buf], file.name || 'photo', {
        type: file.type || 'application/octet-stream',
      });
      setAttachment(inMemory);
    } catch {
      setError(
        "Couldn't read that image. Pick it again, or try a different one " +
          '(some gallery apps hand over a file that expires immediately).',
      );
    }
  }

  function openImagePicker() {
    // iOS Safari has a long-standing bug where <input type="file"> is
    // broken inside an installed PWA (display-mode: standalone). The
    // file picker just never opens. Detect that case and steer the user.
    const inStandalone =
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches) ||
      Boolean((navigator as { standalone?: boolean }).standalone);
    const isIosSafari =
      /iPhone|iPad|iPod/.test(navigator.userAgent) &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (inStandalone && isIosSafari) {
      setError(
        "iOS Safari has a bug where picking files doesn't work from " +
          "installed web apps. Open app.norush.chat in Safari directly " +
          'to attach images. Apple is aware; we hope they fix it soon.',
      );
      return;
    }
    try {
      fileInputRef.current?.click();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function send() {
    const body = draft.trim();
    if (!body && !attachment) return;
    setBusy(true);
    setError(null);
    try {
      let media: MediaRef | undefined;
      if (attachment) {
        media = await encryptAndUpload(attachment);
      }
      const sent_at = new Date().toISOString();
      const messageId = crypto.randomUUID();

      // Build the reply context (if any) once — same for 1:1 and group.
      const reply_to = replyTo
        ? {
            to_message_id: replyTo.id,
            sender_username:
              replyTo.direction === 'out'
                ? me.username
                : replyTo.group_sender ??
                  (subject.kind === 'friend' ? subject.friend.username : '?'),
            body_preview: previewOf(replyTo),
          }
        : undefined;

      const scheduled_for = deliverAt ? deliverAt.toISOString() : undefined;

      if (subject.kind === 'friend') {
        // Make sure the friend actually has a device to receive on. If our
        // cached list is empty (e.g. they hadn't activated when you added
        // them), re-fetch in case they've set up the app since. Still
        // nothing → tell the sender instead of silently dropping it.
        let devices = subject.friend.devices;
        if (!devices || devices.length === 0) {
          try {
            devices = await fetchUserDevices(subject.friend.id);
          } catch {
            // fall through to the guard below
          }
        }
        if (!devices || devices.length === 0) {
          throw new Error(
            `@${subject.friend.username} hasn't set up No Rush on a device yet, ` +
              'so they can\'t receive messages. Ask them to open the app and ' +
              'verify their phone, then try again.',
          );
        }
        await sendToDevices({
          me,
          recipientDevices: devices,
          deliverAt,
          payload: {
            sender_username: me.username,
            body,
            sent_at,
            message_id: messageId,
            media,
            reply_to,
          },
        });
        onSent({
          id: messageId,
          friend_username: subject.friend.username,
          direction: 'out',
          body,
          sent_at,
          media,
          reply_to,
          scheduled_for,
        });
      } else {
        const group = subject.group;
        const allDevices = group.members.flatMap((m) => m.devices);
        if (allDevices.length === 0) {
          throw new Error(
            'No one in this group has set up No Rush on a device yet, so ' +
              'there\'s nobody to deliver to.',
          );
        }
        await sendToDevices({
          me,
          recipientDevices: allDevices,
          deliverAt,
          payload: {
            sender_username: me.username,
            body,
            sent_at,
            message_id: messageId,
            group: { id: group.id, name: group.name },
            media,
            reply_to,
          },
        });
        onSent({
          id: messageId,
          group_id: group.id,
          direction: 'out',
          body,
          sent_at,
          group_sender: me.username,
          media,
          reply_to,
          scheduled_for,
        });
      }
      setDraft('');
      setAttachment(null);
      setReplyTo(null);
      setDeliverAt(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function startReply(m: Message) {
    setReplyTo(m);
    // Focus the composer so the user can start typing immediately.
    window.setTimeout(() => composerRef.current?.focus(), 0);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    void send();
  }

  async function sendReaction(message: Message, emoji: string) {
    // Reaction envelope: no body, no media, just the reaction field.
    const sent_at = new Date().toISOString();
    try {
      if (subject.kind === 'friend') {
        await sendToDevices({
          me,
          recipientDevices: subject.friend.devices,
          payload: {
            sender_username: me.username,
            body: '',
            sent_at,
            reaction: { to_message_id: message.id, emoji },
          },
        });
      } else {
        const group = subject.group;
        const allDevices = group.members.flatMap((m) => m.devices);
        await sendToDevices({
          me,
          recipientDevices: allDevices,
          payload: {
            sender_username: me.username,
            body: '',
            sent_at,
            group: { id: group.id, name: group.name },
            reaction: { to_message_id: message.id, emoji },
          },
        });
      }
    } catch (err) {
      console.error('reaction send failed:', err);
    }
  }

  return (
    <div className="screen screen-conversation">
      <header className="topbar">
        <button className="btn btn-ghost btn-small" onClick={onBack} aria-label="Back">
          ← Back
        </button>
        <div className="topbar-center">
          <span className="friend-avatar friend-avatar-sm">
            {title.replace(/^@/, '')[0]?.toUpperCase() ?? '?'}
          </span>
          <span className="me-name">{title}</span>
        </div>
        <span className="topbar-spacer" />
      </header>

      <div className="bubbles-scroll" ref={scrollRef}>
        <div className="bubbles-inner">
          <p className="conversation-hint">
            no status &middot; no rush &middot;{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => setDetailsOpen((v) => !v)}
            >
              {detailsOpen
                ? 'hide details'
                : subject.kind === 'friend'
                  ? 'safety number'
                  : 'members'}
            </button>
          </p>
          {detailsOpen && subject.kind === 'friend' && (
            <div className="safety-card">
              <p className="safety-label">
                You &harr; @{subject.friend.username}
              </p>
              {safety.kind === 'loading' && <p className="muted">Checking…</p>}
              {safety.kind === 'ok' && (
                <>
                  {safety.entries.map((e, i) => (
                    <div key={e.device_id} style={i > 0 ? { marginTop: 14 } : undefined}>
                      {safety.entries.length > 1 && (
                        <p className="safety-label">{e.label}</p>
                      )}
                      <p className="safety-emojis" aria-label="safety emoji">
                        {e.emojis.join(' ')}
                      </p>
                      {showDigits && (
                        <p className="safety-digits">{formatSafetyDigits(e.digits)}</p>
                      )}
                    </div>
                  ))}
                  <p className="safety-help">
                    If these match on both phones, no one's in the middle.
                    Compare in person or on a call.{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setShowDigits((v) => !v)}
                    >
                      {showDigits ? 'hide numbers' : 'show numbers'}
                    </button>
                  </p>
                </>
              )}
              {safety.kind === 'no_devices' && (
                <p className="safety-help">
                  @{subject.friend.username} hasn't opened the app yet.
                </p>
              )}
              {safety.kind === 'error' && <p className="error">{safety.message}</p>}
            </div>
          )}
          {detailsOpen && subject.kind === 'group' && (
            <GroupDetails me={me} subject={subject} subtitle={subtitle} />
          )}

          {messages.length === 0 && (
            <p className="muted center">
              No messages yet. Write whenever you like.
            </p>
          )}
          {messages.map((m) => (
            <Bubble
              key={m.id}
              message={m}
              showAuthor={subject.kind === 'group'}
              myUsername={me.username}
              onReact={(emoji) => {
                onReact(m, emoji);
                void sendReaction(m, emoji);
              }}
              onOpenLetter={() => setOpenLetter(m)}
              onReply={() => startReply(m)}
              subjectForLabel={subject}
            />
          ))}
        </div>
      </div>

      {replyTo && (
        <div className="composer-reply">
          <span className="composer-reply-label">
            Replying to{' '}
            {replyTo.direction === 'out'
              ? 'yourself'
              : `@${
                  replyTo.group_sender ??
                  (subject.kind === 'friend' ? subject.friend.username : '?')
                }`}
          </span>
          <span className="composer-reply-preview">{previewOf(replyTo)}</span>
          <button
            type="button"
            className="btn btn-ghost btn-small"
            onClick={() => setReplyTo(null)}
            aria-label="Cancel reply"
          >
            ×
          </button>
        </div>
      )}

      {attachment && attachmentPreviewUrl && (
        <div className="composer-attachment">
          {attachment.type.startsWith('image/') ? (
            <img src={attachmentPreviewUrl} alt="attachment preview" />
          ) : attachment.type.startsWith('audio/') ? (
            // Reuse the real AudioBubble (decodes the true duration via
            // WebAudio, so it shows the right length + a working scrubber)
            // instead of a native <audio controls>, which hits the WebM
            // Infinity-duration bug and shows 0:00 with a jumpy seek bar.
            <AudioBubble
              url={attachmentPreviewUrl}
              messageId="composer-preview"
              allowTranscribe={false}
            />
          ) : (
            <span className="muted">{attachment.name}</span>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-small"
            onClick={() => setAttachment(null)}
          >
            Remove
          </button>
        </div>
      )}

      <form onSubmit={onSubmit} className="composer-row">
        {!recording && (
          <textarea
            ref={composerRef}
            className="composer-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={attachment ? 'Add a caption (optional)…' : 'Write whenever you like…'}
            rows={1}
            disabled={busy}
            onKeyDown={(e) => {
              // Enter sends only on a real keyboard (e.g. desktop). On mobile
              // virtual keyboards Enter is almost always the user wanting a
              // newline, so we let it through. shiftKey forces a newline on
              // desktop too.
              const isDesktopEnter =
                e.key === 'Enter' && !e.shiftKey && !isLikelyMobile();
              if (isDesktopEnter) {
                e.preventDefault();
                void send();
              }
            }}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
        <div className="composer-actions">
          {/* While recording, the recorder takes the whole bar; the other
              actions hide so the waveform + Stop/Cancel have room. */}
          {!recording && (
            <button
              type="button"
              className="btn btn-ghost composer-attach-btn"
              onClick={openImagePicker}
              disabled={busy}
              aria-label="Attach photo"
              title="Attach photo"
            >
              📎
            </button>
          )}
          <VoiceRecorder
            onCaptured={(file) => setAttachment(file)}
            onCancel={() => undefined}
            onRecordingChange={setRecording}
            disabled={busy}
          />
          {!recording && (
            <>
              <button
                type="button"
                className="btn btn-ghost composer-attach-btn"
                onClick={() => setLetterMode(true)}
                disabled={busy}
                aria-label="Write a letter"
                title="Write a letter (long-form, paper-and-serif on the other side)"
              >
                ✉️
              </button>
              <button
                type="button"
                className={`btn btn-ghost composer-attach-btn ${emojiOpen ? 'on' : ''}`}
                onClick={() => setEmojiOpen((v) => !v)}
                disabled={busy}
                aria-label="Insert emoji"
                aria-expanded={emojiOpen}
                title="Insert emoji"
              >
                😊
              </button>
              <ScheduleControl value={deliverAt} onChange={setDeliverAt} disabled={busy} />
              <span className="composer-spacer" />
              <button
                type="submit"
                className="btn btn-primary composer-send"
                disabled={busy || (!draft.trim() && !attachment)}
              >
                {busy ? 'Sealing…' : deliverAt ? 'Schedule' : 'Send'}
              </button>
            </>
          )}
        </div>
      </form>
      {emojiOpen && (
        <div className="composer-emoji-panel" role="dialog" aria-label="Emoji picker">
          {COMPOSER_EMOJI.map((emoji) => (
            <button
              type="button"
              key={emoji}
              className="composer-emoji-btn"
              onClick={() => insertEmoji(emoji)}
              aria-label={`Insert ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
      {error && <p className="error error-inline">{error}</p>}

      {letterMode && (
        <LetterCompose
          me={me}
          subject={subject}
          onSent={(args) => {
            onSent({
              id: args.id,
              friend_username: subject.kind === 'friend' ? subject.friend.username : undefined,
              group_id: subject.kind === 'group' ? subject.group.id : undefined,
              direction: 'out',
              body: args.body,
              sent_at: args.sent_at,
              kind: 'letter',
              title: args.title,
              letter_theme: args.letter_theme,
              letter_font: args.letter_font,
              letter_size: args.letter_size,
              letter_signature: args.letter_signature,
              scheduled_for: args.scheduled_for,
              group_sender: subject.kind === 'group' ? me.username : undefined,
            });
            setLetterMode(false);
          }}
          onClose={() => setLetterMode(false)}
        />
      )}

      {openLetter && (
        <LetterReader
          message={openLetter}
          authorLabel={
            openLetter.direction === 'out'
              ? `from you to ${
                  subject.kind === 'friend'
                    ? '@' + subject.friend.username
                    : subject.group.name
                }`
              : `from @${
                  openLetter.group_sender ??
                  (subject.kind === 'friend' ? subject.friend.username : '?')
                }`
          }
          onClose={() => setOpenLetter(null)}
        />
      )}
    </div>
  );
}

function Bubble({
  message,
  showAuthor,
  myUsername,
  onReact,
  onOpenLetter,
  onReply,
  subjectForLabel,
}: {
  message: Message;
  showAuthor: boolean;
  myUsername: string;
  onReact: (emoji: string) => void;
  onOpenLetter: () => void;
  onReply: () => void;
  subjectForLabel: Subject;
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Long-press anywhere on a bubble opens the reaction palette (in addition
  // to the + button). We start a timer on press and cancel it if the
  // finger lifts or moves first, so a normal tap/scroll doesn't trigger it.
  const pressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);
  const clearPress = () => {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const startPress = () => {
    clearPress();
    longPressFired.current = false;
    pressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      setPaletteOpen(true);
    }, 420);
  };
  // After a long-press, swallow the click that fires on release so it
  // doesn't also (e.g.) open the image lightbox.
  const swallowClickAfterLongPress = (e: React.MouseEvent) => {
    if (longPressFired.current) {
      e.preventDefault();
      e.stopPropagation();
      longPressFired.current = false;
    }
  };

  // Aggregate reactions by emoji: { '❤️': 2, '👍': 1 }
  const reactionCounts = useMemo(() => {
    const map = new Map<string, number>();
    if (message.reactions) {
      for (const emoji of Object.values(message.reactions)) {
        map.set(emoji, (map.get(emoji) ?? 0) + 1);
      }
    }
    return map;
  }, [message.reactions]);

  const myReaction = message.reactions?.[myUsername];
  const isLetter = message.kind === 'letter';

  return (
    <div className={`bubble-wrap ${message.direction === 'out' ? 'out' : 'in'}`}>
      {isLetter ? (
        <LetterCard
          message={message}
          showAuthor={showAuthor}
          onOpen={onOpenLetter}
        />
      ) : (
        <div
          className={`bubble ${message.direction === 'out' ? 'out' : 'in'}`}
          onPointerDown={startPress}
          onPointerUp={clearPress}
          onPointerMove={clearPress}
          onPointerLeave={clearPress}
          onPointerCancel={clearPress}
          onClickCapture={swallowClickAfterLongPress}
          onContextMenu={(e) => {
            // Suppress the browser long-press menu so our reaction palette
            // wins (and the image save menu doesn't fight tap-to-zoom).
            e.preventDefault();
          }}
        >
          {showAuthor && message.direction === 'in' && (
            <div className="bubble-author">@{message.group_sender ?? '?'}</div>
          )}
          {message.reply_to && (
            <div className="bubble-reply-context">
              <span className="bubble-reply-author">
                ↪ @{message.reply_to.sender_username}
              </span>
              <span className="bubble-reply-preview">
                {message.reply_to.body_preview}
              </span>
            </div>
          )}
          {message.media && <MediaThumbnail media={message.media} messageId={message.id} />}
          {message.body && <div className="bubble-body">{message.body}</div>}
          {message.scheduled_for && (
            <div className="bubble-scheduled">
              🕊️ arrives {formatDelivery(new Date(message.scheduled_for))}
            </div>
          )}
        </div>
      )}

      {reactionCounts.size > 0 && (
        <div className="bubble-reactions">
          {[...reactionCounts.entries()].map(([emoji, count]) => (
            <span
              key={emoji}
              className={`reaction-badge ${myReaction === emoji ? 'mine' : ''}`}
              title={`${count} reaction${count > 1 ? 's' : ''}`}
            >
              {emoji}
              {count > 1 && <span className="reaction-count">{count}</span>}
            </span>
          ))}
        </div>
      )}

      <div className="bubble-react-row">
        <button
          type="button"
          className="react-trigger"
          onClick={onReply}
          aria-label="Reply"
          title="Reply"
        >
          ↪
        </button>
        <button
          type="button"
          className="react-trigger"
          onClick={() => setPaletteOpen((v) => !v)}
          aria-label="React"
          title="React"
        >
          {paletteOpen ? '×' : '+'}
        </button>
        {paletteOpen && (
          <div className="react-palette" role="menu">
            {REACTION_PALETTE.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className={`react-palette-btn ${myReaction === emoji ? 'mine' : ''}`}
                onClick={() => {
                  onReact(emoji);
                  setPaletteOpen(false);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* subjectForLabel is reserved for future "who exactly are we
          replying to" surfacing (e.g. group context). Currently used
          only via the Bubble closure for prop completeness. */}
      <span style={{ display: 'none' }} aria-hidden="true">
        {subjectForLabel.kind}
      </span>
    </div>
  );
}

function MediaThumbnail({ media, messageId }: { media: MediaRef; messageId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        // Lazy import to avoid loading media lib until we actually need it.
        const { downloadAndDecrypt } = await import('../lib/media');
        const blob = await downloadAndDecrypt(media);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setUrl(createdUrl);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [media]);

  if (error) return <div className="media-error">[ media decrypt failed: {error} ]</div>;
  if (loading) return <div className="media-loading">decrypting…</div>;
  if (!url) return null;

  if (media.mime.startsWith('image/')) {
    return (
      <>
        <img
          className="bubble-media"
          src={url}
          alt=""
          onClick={() => setZoom(true)}
        />
        {zoom && (
          <div
            className="image-lightbox"
            role="dialog"
            aria-modal="true"
            onClick={() => setZoom(false)}
          >
            <img src={url} alt="" />
            <a
              className="image-lightbox-save btn btn-ghost btn-small"
              href={url}
              download
              onClick={(e) => e.stopPropagation()}
            >
              Save
            </a>
          </div>
        )}
      </>
    );
  }
  if (media.mime.startsWith('audio/')) {
    return <AudioBubble url={url} messageId={messageId} />;
  }
  return (
    <a className="bubble-media-link" href={url} download>
      Encrypted file ({media.size} bytes)
    </a>
  );
}

type TranscribePhase =
  | { kind: 'idle' }
  | { kind: 'downloading'; loadedMB: number; totalMB: number }
  | { kind: 'preparing' }
  | { kind: 'transcribing' }
  | { kind: 'error'; message: string };

function AudioBubble({
  url,
  messageId,
  allowTranscribe = true,
}: {
  url: string;
  messageId: string;
  allowTranscribe?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [transcript, setTranscriptState] = useState<string | null>(null);
  const [transcribePhase, setTranscribePhase] = useState<TranscribePhase>({ kind: 'idle' });

  // Load any previously-saved transcript for this message from localStorage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { loadTranscript } = await import('../lib/transcribe');
        const cached = loadTranscript(messageId);
        if (cached && !cancelled) setTranscriptState(cached);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messageId]);

  async function doTranscribe() {
    setTranscribePhase({ kind: 'preparing' });
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const { transcribe, saveTranscript } = await import('../lib/transcribe');
      const text = await transcribe(blob, (p) => {
        if (p.status === 'downloading') {
          setTranscribePhase({
            kind: 'downloading',
            loadedMB: p.loadedMB,
            totalMB: p.totalMB,
          });
        } else if (p.status === 'preparing') {
          setTranscribePhase({ kind: 'preparing' });
        } else if (p.status === 'transcribing') {
          setTranscribePhase({ kind: 'transcribing' });
        }
      });
      saveTranscript(messageId, text);
      setTranscriptState(text);
      setTranscribePhase({ kind: 'idle' });
    } catch (err) {
      setTranscribePhase({
        kind: 'error',
        message: errorMessage(err),
      });
    }
  }

  // Decode the audio once: compute waveform peaks AND the real duration.
  // The <audio> element's duration is unreliable for MediaRecorder
  // output (often Infinity because WebM streams skip the duration
  // header field), so we rely on the decoded AudioBuffer's duration.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const { computeWaveform } = await import('../lib/waveform');
        const result = await computeWaveform(blob);
        if (cancelled || !result) {
          if (!cancelled) setPeaks(null);
          return;
        }
        setPeaks(result.peaks);
        if (isFinite(result.duration) && result.duration > 0) {
          setDuration(result.duration);
        }
      } catch {
        // ignore — peaks stays null, plain bar fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Fallback duration probe for the <audio> element: WebM MediaRecorder
  // files report duration = Infinity until the audio has been "seeked"
  // to the end. We do that once on metadata-load so the native player
  // also knows the real duration (useful for seeking, end events).
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoadedMeta = () => {
      if (!isFinite(a.duration)) {
        const onSeeked = () => {
          a.currentTime = 0;
          a.removeEventListener('seeked', onSeeked);
        };
        a.addEventListener('seeked', onSeeked);
        try {
          a.currentTime = 1e9;
        } catch {
          // ignore
        }
      }
    };
    a.addEventListener('loadedmetadata', onLoadedMeta);
    return () => a.removeEventListener('loadedmetadata', onLoadedMeta);
  }, []);

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      void a.play().catch(() => undefined);
    }
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current;
    const bar = barRef.current;
    if (!a || !bar || !isFinite(duration) || duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
  }

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  return (
    <div className="audio-bubble">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (isFinite(d)) setDuration(d);
        }}
        onDurationChange={(e) => {
          const d = e.currentTarget.duration;
          if (isFinite(d)) setDuration(d);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <button
        type="button"
        className="audio-play"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play voice note'}
      >
        {playing ? (
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <rect x="3" y="2" width="3.5" height="12" rx="1" fill="currentColor" />
            <rect x="9.5" y="2" width="3.5" height="12" rx="1" fill="currentColor" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M3.5 2.5 L13 8 L3.5 13.5 Z" fill="currentColor" />
          </svg>
        )}
      </button>
      {peaks ? (
        <div className="audio-waveform" ref={barRef} onClick={seek}>
          {peaks.map((p, i) => {
            const played = (i + 0.5) / peaks.length <= progress;
            return (
              <span
                key={i}
                className={`audio-waveform-bar ${played ? 'played' : ''}`}
                style={{ height: `${Math.max(12, p * 100)}%` }}
              />
            );
          })}
        </div>
      ) : (
        <div className="audio-progress" ref={barRef} onClick={seek}>
          <div
            className="audio-progress-fill"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}
      <div className="audio-time">
        {formatDuration(playing || currentTime > 0 ? currentTime : duration)}
      </div>
      {allowTranscribe && (
        <TranscribeAffordance
          transcript={transcript}
          phase={transcribePhase}
          onStart={doTranscribe}
        />
      )}
    </div>
  );
}

function TranscribeAffordance({
  transcript,
  phase,
  onStart,
}: {
  transcript: string | null;
  phase: TranscribePhase;
  onStart: () => void;
}) {
  // The transcript and the progress live in a small block tucked under the
  // audio bubble row. This component is rendered INSIDE the .audio-bubble
  // flex container, so it needs flex-basis 100% to wrap below the controls.
  if (transcript) {
    return (
      <div className="audio-transcript">
        <span className="audio-transcript-label">Transcript</span>
        <span className="audio-transcript-body">{transcript}</span>
      </div>
    );
  }

  if (phase.kind === 'idle') {
    return (
      <button
        type="button"
        className="audio-transcribe-trigger"
        onClick={onStart}
      >
        Transcribe
      </button>
    );
  }

  if (phase.kind === 'downloading') {
    const pct = phase.totalMB > 0 ? (phase.loadedMB / phase.totalMB) * 100 : 0;
    return (
      <div className="audio-transcript-progress">
        <span>
          Downloading speech model (first time, on this device only) ·{' '}
          {phase.loadedMB.toFixed(1)} / {phase.totalMB.toFixed(1)} MB
        </span>
        <div className="audio-transcript-progress-bar">
          <div
            className="audio-transcript-progress-fill"
            style={{ width: `${pct.toFixed(1)}%` }}
          />
        </div>
      </div>
    );
  }

  if (phase.kind === 'preparing') {
    return (
      <div className="audio-transcript-progress">
        <span>Preparing model…</span>
      </div>
    );
  }

  if (phase.kind === 'transcribing') {
    return (
      <div className="audio-transcript-progress">
        <span>Transcribing on your device…</span>
      </div>
    );
  }

  return (
    <div className="audio-transcript-progress">
      <span className="error">
        Transcription failed: {phase.message}.{' '}
        <button
          type="button"
          className="link-button"
          onClick={onStart}
        >
          Retry
        </button>
        . If it keeps failing, open the browser console (F12 → Console) and
        share the [no-rush] error line — it shows which URL got blocked.
      </span>
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Heuristic: are we on a mobile device with an on-screen keyboard? Used so
 * the Enter key inserts a newline (mobile convention) rather than sending
 * the message. Desktops keep "Enter sends, Shift+Enter newlines."
 */
function isLikelyMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Modern hint: the visual viewport shrinks when the OS keyboard opens, and
  // most mobile UAs include one of these tokens.
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function previewOf(m: Message): string {
  if (m.kind === 'letter' && m.title) return `📜 ${m.title}`;
  if (m.body) {
    const clean = m.body.replace(/\s+/g, ' ').trim();
    return clean.length > 100 ? clean.slice(0, 100) + '…' : clean;
  }
  if (m.media?.mime.startsWith('image/')) return '📷 Photo';
  if (m.media?.mime.startsWith('audio/')) return '🎙️ Voice note';
  if (m.media) return '📎 File';
  return '…';
}

function LetterCard({
  message,
  showAuthor,
  onOpen,
}: {
  message: Message;
  showAuthor: boolean;
  onOpen: () => void;
}) {
  const words = message.body.trim()
    ? message.body.trim().split(/\s+/).length
    : 0;
  const mins = Math.max(1, Math.round(words / 220));
  const preview = message.body.replace(/\s+/g, ' ').trim().slice(0, 140);

  const authorLabel =
    message.direction === 'out'
      ? 'A letter from you'
      : showAuthor
        ? `A letter from @${message.group_sender ?? '?'}`
        : 'A letter';

  return (
    <button
      type="button"
      className={`letter-card ${message.direction === 'out' ? 'out' : 'in'}`}
      onClick={onOpen}
    >
      <span className="letter-card-meta">{authorLabel}</span>
      {message.title && <span className="letter-card-title">{message.title}</span>}
      <span className="letter-card-preview">{preview}{message.body.length > 140 ? '…' : ''}</span>
      <span className="letter-card-foot">
        <span>{mins} min read · {words} words</span>
        <span className="letter-card-cta">Open letter →</span>
      </span>
    </button>
  );
}

function GroupDetails({
  me,
  subject,
  subtitle,
}: {
  me: Identity;
  subject: Extract<Subject, { kind: 'group' }>;
  subtitle: string | null;
}) {
  const [showQr, setShowQr] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // memberId → truncated 11-char safety prefix, or '…' while loading, or '—'
  // when the member hasn't published a Signal bundle yet.
  const [safeties, setSafeties] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const store = getSignalStore(me.id);
        const myKp = await store.getIdentityKeyPair();
        if (!myKp) return;
        const updates: Record<string, string> = {};
        for (const m of subject.group.members) {
          if (cancelled) return;
          // A compact 4-emoji safety summary per member, from their first
          // device's identity key. The 1:1 conversation card shows the full
          // emoji row + numbers.
          const firstDevice = m.devices?.[0];
          if (!firstDevice) {
            updates[m.id] = '—';
            continue;
          }
          try {
            const myKeyB64 = arrayBufferToBase64(myKp.pubKey);
            updates[m.id] = safetyEmojis(
              myKeyB64,
              firstDevice.signal_identity_pubkey_b64,
              4,
            ).join(' ');
          } catch {
            updates[m.id] = '?';
          }
        }
        if (!cancelled) setSafeties(updates);
      } catch {
        // Local Signal identity not loaded yet — leave safeties empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me.id, me.device_id, subject.group]);

  const sharePayload = useMemo(
    () =>
      encodeGroupShareUrl({
        ...subject.group,
        // Include me in the shared spec, so a joiner sees the full group.
        // The joiner will fetch each member's devices fresh on scan.
        members: [
          {
            id: me.id,
            username: me.username,
            devices: [],
          },
          ...subject.group.members,
        ],
      }),
    [me.id, me.username, subject.group],
  );

  useEffect(() => {
    if (!showQr) return;
    QRCode.toDataURL(sharePayload, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 220,
      color: { dark: '#2b2a26', light: '#fbf9f4' },
    })
      .then(setQrDataUrl)
      .catch((e) => setError(errorMessage(e)));
  }, [showQr, sharePayload]);

  return (
    <div className="safety-card">
      <p className="safety-label">{subtitle}</p>
      <ul className="member-list-inline">
        <li>@{me.username} (you)</li>
        {subject.group.members.map((m) => (
          <li key={m.id}>
            @{m.username}
            <span className="member-safety">
              &nbsp;· safety {safeties[m.id] ?? '…'}
            </span>
          </li>
        ))}
      </ul>
      <p className="safety-help">
        Each message is encrypted separately to each member. The relay
        sees N independent envelopes, never the group as a unit.
      </p>
      <button
        type="button"
        className="btn btn-ghost btn-small"
        onClick={() => setShowQr((v) => !v)}
        style={{ marginTop: 10 }}
      >
        {showQr ? 'Hide invite QR' : 'Show invite QR'}
      </button>
      {showQr && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          {error && <p className="error">{error}</p>}
          {qrDataUrl && (
            <img
              src={qrDataUrl}
              alt={`QR invite for group ${subject.group.name}`}
              className="qr-img"
            />
          )}
          <p className="safety-help">
            A friend scans this on the "Add by QR" → "Scan" page and joins
            the group. The QR contains the group name, member usernames,
            and member public keys — nothing secret.
          </p>
        </div>
      )}
    </div>
  );
}
